import { editDistance, soundKey, soundWords } from "./phonetic";

/** Sounds at the end of the form that must match exactly: grammar lives in endings. */
const ENDING_LENGTH = 3;

/**
 * How far off a heard form may be and still count. Grammatical forms often
 * differ by one sound at the end (γράφει / γράφεις, τακτοποίησα / τακτοποίησε),
 * so the ending must match exactly and only a long stem may carry a
 * recognition slip or two.
 */
function closeEnough(heard: string, target: string): boolean {
    if (heard === target)
        return true;
    if (heard.slice(-ENDING_LENGTH) !== target.slice(-ENDING_LENGTH))
        return false;
    const allowed = target.length < 6 ? 0 : target.length < 12 ? 1 : 2;
    return editDistance(heard, target) <= allowed;
}

/**
 * Whether a spoken transcript gives the expected answer. Compared by sound
 * (see phonetic.ts), so homophone spellings, a wrong script and a missing
 * accent all pass; the form must be there, said alone or inside a sentence
 * ("Χθες έγραψα ένα γράμμα" answers "έγραψα"). The recognizer may split or
 * join words ("θαγράψω"), so word boundaries are ignored.
 */
export function answerMatches(expected: string, transcript: string): boolean {
    const target = soundKey(expected).replace(/ /g, '');
    if (target.length === 0)
        return false;

    const expectedWords = soundWords(expected).length;
    const heard = soundWords(transcript);

    for (let start = 0; start < heard.length; start++) {
        for (let length = 1; length <= expectedWords + 1 && start + length <= heard.length; length++) {
            if (closeEnough(heard.slice(start, start + length).join(''), target))
                return true;
        }
    }
    return false;
}

/** True when any transcript (one per recognition language) gives any of the accepted answers. */
export function isCorrectAnswer(accepted: string[], transcripts: string[]): boolean {
    return accepted.some(answer => transcripts.some(transcript => answerMatches(answer, transcript)));
}
